/**
 * useOfflineCachedOutbreak — reads the IndexedDB cached outbreak payload
 * when the device is offline OR when the SSE stream has been silent >15 s.
 *
 * Usage:
 *   const { isDegraded, cachedData, cachedAt } = useOfflineCachedOutbreak();
 *
 * - `isDegraded === true` when !online || sseHealth === 'disconnected'
 * - `cachedData`  is the last successful /api/outbreak payload (or null)
 * - `cachedAt`    is the Unix ms timestamp of the last successful fetch
 *
 * The cache is written by `apps/web/src/services/outbreak.ts` on every
 * successful network fetch (fire-and-forget, never blocks UI).
 * The service worker (prompt 25) also writes the cache on every successful
 * network response — providing a second write path for navigation loads.
 */
import { useQuery } from "@tanstack/react-query";
import { useOnlineStatus } from "#/hooks/useOnlineStatus.js";
import { useSSEHealth } from "#/hooks/useSSEHealth.js";
import { getCachedOutbreak } from "#/lib/idb/outbreak-cache.js";
import type { OutbreakResponse } from "#/services/outbreak.js";

export interface OfflineCacheResult {
  /**
   * True when the device is offline OR the SSE stream has been silent
   * for >30 s (disconnected state). Triggers the degraded UI.
   */
  isDegraded: boolean;
  /** Last cached outbreak payload, or null if no cache exists yet. */
  cachedData: OutbreakResponse | null;
  /** Unix ms timestamp of the cached payload (null if no cache). */
  cachedAt: number | null;
}

export function useOfflineCachedOutbreak(): OfflineCacheResult {
  const online = useOnlineStatus();
  const sseHealth = useSSEHealth();
  const isDegraded = !online || sseHealth === "disconnected";

  const { data } = useQuery({
    queryKey: ["offline-outbreak-cache"],
    queryFn: getCachedOutbreak,
    // Only read from IDB when we're in the degraded state.
    enabled: isDegraded,
    // Stale-time of 0 so it re-reads IDB on every degraded entry.
    staleTime: 0,
    // Never refetch on window focus — we control when to re-read.
    refetchOnWindowFocus: false,
  });

  return {
    isDegraded,
    cachedData: data?.payload ?? null,
    cachedAt: data?.fetchedAt ?? null,
  };
}
